<div class="space-y-6">
  <div class="flex items-center justify-between">
    <div class="prose">
      <h1>{{ 'pages.addressHistory.title' | translate }}</h1>
    </div>
    @if (fullHistory && fullHistory.length > 1) {
      <div class="flex gap-2" data-test-id="historySwitchContainer">
        <nz-switch
          data-test-id="historySwitch"
          [ngModel]="showHistory"
          (ngModelChange)="onShowHistoryChange($event)"
        />
        {{ 'pages.addressHistory.historySwitch.label' | translate }}
      </div>
    }
  </div>

  <div class="flex items-start gap-6">
    @if (fullHistory?.length) {
      <div
        class="w-1/4 min-w-84 space-y-1 rounded-xl bg-grey-50 px-4 py-3"
        data-test-id="historyList"
      >
        <h2 class="text-lg">
          {{ 'pages.addressHistory.historyList.title' | translate }}
        </h2>
        @for (item of fullHistory; track item.referenceId) {
          <div
            class="border-t-1 border-grey-300 pt-2 text-sm"
            data-test-id="historyItem"
          >
            <div class="mb-2 font-bold" data-test-id="historyVersion">
              {{ 'pages.addressHistory.historyList.version' | translate }}
              {{ item.versionNumber }}:
            </div>
            <div class="mb-2">
              <p class="flex gap-2 whitespace-nowrap">
                <svg-icon
                  src="assets/icons/Time/calendar-date.svg"
                  class="size-4"
                />
                {{ item.mergedAt | date: 'dd.MM.yyyy - HH:mm' }}
              </p>
              <p class="flex gap-2">
                <svg-icon
                  src="assets/icons/Users/user-circle.svg"
                  class="size-4"
                />
                {{ item.mergedBy }}
              </p>
            </div>
            <div class="mb-2 font-bold">
              {{ 'pages.addressHistory.historyList.upload' | translate }}:
            </div>
            <div class="mb-2">
              <p class="flex gap-2 whitespace-nowrap">
                <svg-icon
                  src="assets/icons/Time/calendar-date.svg"
                  class="size-4"
                />
                {{ item.createdAt | date: 'dd.MM.yyyy - HH:mm' }}
              </p>
              <p class="flex gap-2">
                <svg-icon
                  src="assets/icons/Users/user-circle.svg"
                  class="size-4"
                />
                {{ item.createdBy }}
              </p>
            </div>
            <div>
              <span class="after:mx-2 after:content-['•']">
                {{ 'models.branch.source.label' | translate }}:
                {{ item.source }}
              </span>
              <span>
                {{ 'models.branch.reason.label' | translate }}:
                {{ item.reason }}
              </span>
            </div>
            @if (item.note) {
              <div>{{ 'models.branch.note.label' | translate }}:</div>
              <div>
                {{ item.note }}
              </div>
            }
          </div>
        }
      </div>
    }
    <div class="flex w-3/4 flex-col gap-6">
      <div class="flex max-w-screen-xl flex-col gap-2"></div>
      @if (showHistory) {
        <table data-test-id="historyComparisonTable">
          <thead>
            <tr>
              <th class="align-middle font-normal">
                <nz-switch
                  class="mr-2"
                  data-test-id="showOnlyChangedSwitch"
                  [(ngModel)]="showOnlyChanged"
                />
                {{ 'pages.addressHistory.differencesOnly.label' | translate }}
              </th>
              <th>
                <form
                  data-test-id="leftFilterForm"
                  class="-mb-6"
                  [formGroup]="filterForm"
                >
                  <formly-form
                    [form]="filterForm"
                    [fields]="[leftFilterField]"
                    [model]="filterModel"
                    (modelChange)="onFilterChange()"
                  />
                </form>
              </th>
              <th>
                <form
                  data-test-id="rightFilterForm"
                  class="-mb-6"
                  [formGroup]="filterForm"
                >
                  <formly-form
                    [form]="filterForm"
                    [fields]="[rightFilterField]"
                    [model]="filterModel"
                    (modelChange)="onFilterChange()"
                  />
                </form>
              </th>
            </tr>
          </thead>
          <ng-container
            [ngTemplateOutlet]="historyBody"
            [ngTemplateOutletContext]="{ rows: masterData, master: true }"
          />
          <ng-container
            [ngTemplateOutlet]="historyBody"
            [ngTemplateOutletContext]="{ rows: dynamicData, master: false }"
          />
        </table>
      } @else {
        <ng-container
          data-test-id="simpleTableContainer"
          [ngTemplateOutlet]="tableTemplate"
          [ngTemplateOutletContext]="{ rows: masterData, master: true }"
        />
        <ng-container
          [ngTemplateOutlet]="tableTemplate"
          [ngTemplateOutletContext]="{ rows: dynamicData, master: false }"
        />
      }
    </div>
  </div>
</div>

<ng-template
  #historyBody
  let-master="master"
  let-rows="rows"
  [typedTemplate]="tableTypeToken"
>
  <thead [attr.data-test-id]="master ? 'masterDataBody' : 'dynamicDataBody'">
    <tr>
      <th id="groupHeader" colspan="3">
        <h2 class="text-2xl">
          @if (master) {
            {{ 'pages.addressHistory.cluster.masterData' | translate }}
          } @else {
            {{ 'pages.addressHistory.cluster.dynamicData' | translate }}
          }
        </h2>
      </th>
    </tr>
  </thead>
  <tbody>
    @for (data of rows; track data.key) {
      @let changed = data.leftValue !== data.rightValue;
      @if (!showOnlyChanged || changed) {
        <tr class="border-b-1 border-grey-200" data-test-id="attributeRow">
          <td>
            <ng-container
              [ngTemplateOutlet]="keyCellTemplate"
              [ngTemplateOutletContext]="{ key: data.key, master: master }"
            />
          </td>
          @if (isLeftLoading) {
            @if ($first) {
              <td class="align-top" [rowSpan]="$count">
                <svg-icon
                  data-test-id="leftLoadingIcon"
                  src="assets/icons/General/loading-01.svg"
                  class="animate-spin"
                />
              </td>
            }
          } @else {
            <td>
              <ng-container
                [ngTemplateOutlet]="valueCellTemplate"
                [ngTemplateOutletContext]="{ value: data.leftValue, changed }"
              />
            </td>
          }
          @if (isRightLoading) {
            @if ($first) {
              <td>
                <svg-icon
                  data-test-id="rightLoadingIcon"
                  src="assets/icons/General/loading-01.svg"
                  class="animate-spin"
                />
              </td>
            } @else if ($index === 1) {
              <td [rowSpan]="$count - 1"></td>
            }
          } @else {
            <td>
              <ng-container
                [ngTemplateOutlet]="valueCellTemplate"
                [ngTemplateOutletContext]="{ value: data.rightValue }"
              />
            </td>
          }
        </tr>
      }
    }
  </tbody>
</ng-template>

<ng-template
  #tableTemplate
  let-master="master"
  let-rows="rows"
  [typedTemplate]="tableTypeToken"
>
  <div
    [attr.data-test-id]="master ? 'masterDataSection' : 'dynamicDataSection'"
  >
    <h2 class="text-2xl">
      @if (master) {
        {{ 'pages.addressHistory.cluster.masterData' | translate }}
      } @else {
        {{ 'pages.addressHistory.cluster.dynamicData' | translate }}
      }
    </h2>
    @if (isLoading) {
      <svg-icon
        data-test-id="loadingIcon"
        src="assets/icons/General/loading-01.svg"
        class="animate-spin"
      />
    } @else {
      <table data-test-id="simpleTable">
        <thead>
          <tr class="sr-only">
            <th scope="col">
              {{ 'pages.addressHistory.table.key' | translate }}
            </th>
            <th scope="col">
              {{ 'pages.addressHistory.table.value' | translate }}
            </th>
          </tr>
        </thead>
        <tbody>
          @for (data of rows; track data.key) {
            <tr
              class="border-b-1 border-grey-200"
              data-test-id="simpleAttributeRow"
            >
              <td>
                <ng-container
                  [ngTemplateOutlet]="keyCellTemplate"
                  [ngTemplateOutletContext]="{ key: data.key, master: master }"
                />
              </td>
              <td>
                <ng-container
                  [ngTemplateOutlet]="valueCellTemplate"
                  [ngTemplateOutletContext]="{ value: data.latestValue }"
                />
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>
</ng-template>

<ng-template
  #keyCellTemplate
  let-master="master"
  let-key="key"
  [typedTemplate]="keyTypeToken"
>
  @let translationKeyQuery = `models.address.${key}.label`;
  @let translatedKey = translationKeyQuery | translate;

  @let translationOewNameQuery = `models.address.${key}.oew_name`;
  @let translatedOewName = translationOewNameQuery | translate;

  @if (master) {
    <strong>{{ translatedKey }}</strong>
  } @else {
    @if (translatedOewName === translationOewNameQuery) {
      {{ key }}<br />
      ##missing oew_name## ({{ translationOewNameQuery }})<br />
    } @else {
      <strong>{{ translatedOewName }}</strong>
    }
  }
</ng-template>

<ng-template
  #valueCellTemplate
  let-value="value"
  let-changed="changed"
  [typedTemplate]="valueTypeToken"
>
  <div [ngClass]="{ 'text-green': changed }">
    @if (value === null || value === undefined) {
      -
    } @else {
      {{ value }}
    }
  </div>
</ng-template>
